import api from '../config/api';
import { isApiEnabled } from '../config/appConfig';
import { getApiData, parseApiError } from './apiUtils';

export const fetchUserProfile = (usuarioId) => {
  if (!usuarioId) {
    return Promise.reject(new Error('Debes iniciar sesión para ver tu perfil.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('El perfil requiere conexión con el servidor.'));
  }

  return api
    .post('/perfil_obtener.php', { usuario_id: usuarioId })
    .then((response) => getApiData(response, 'No se pudo cargar el perfil.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const completeUserProfile = (usuarioId, payload) => {
  if (!usuarioId) {
    return Promise.reject(new Error('Debes iniciar sesión para completar tu perfil.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La actualización requiere conexión con el servidor.'));
  }

  return api
    .post('/perfil_completar.php', {
      usuario_id: usuarioId,
      ...payload,
    })
    .then((response) => getApiData(response, 'No se pudo completar el perfil.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const updateUserProfile = (usuarioId, payload) => {
  if (!usuarioId) {
    return Promise.reject(new Error('Debes iniciar sesión para actualizar tu perfil.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La actualización requiere conexión con el servidor.'));
  }

  return api
    .post('/perfil_actualizar.php', {
      usuario_id: usuarioId,
      ...payload,
    })
    .then((response) => getApiData(response, 'No se pudo actualizar el perfil.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const uploadUserAvatar = (usuarioId, imagenBase64) => {
  if (!usuarioId) {
    return Promise.reject(new Error('Debes iniciar sesión para actualizar tu avatar.'));
  }

  if (!imagenBase64) {
    return Promise.reject(new Error('Selecciona una imagen válida.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La actualización requiere conexión con el servidor.'));
  }

  return api
    .post('/perfil_avatar_guardar.php', {
      usuario_id: usuarioId,
      solicitante_id: usuarioId,
      imagen_base64: imagenBase64,
    })
    .then((response) => getApiData(response, 'No se pudo actualizar el avatar.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const removeUserAvatar = (usuarioId) => {
  if (!usuarioId) {
    return Promise.reject(new Error('Debes iniciar sesión para eliminar tu avatar.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La actualización requiere conexión con el servidor.'));
  }

  return api
    .post('/perfil_avatar_guardar.php', {
      usuario_id: usuarioId,
      solicitante_id: usuarioId,
      eliminar: true,
    })
    .then((response) => getApiData(response, 'No se pudo eliminar el avatar.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const saveUserChildren = (usuarioId, payload) => {
  if (!usuarioId) {
    return Promise.reject(new Error('Debes iniciar sesión para actualizar tus hijos.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La actualización requiere conexión con el servidor.'));
  }

  return api
    .post('/perfil_hijos_guardar.php', {
      usuario_id: usuarioId,
      ...payload,
    })
    .then((response) => getApiData(response, 'No se pudo guardar la información de hijos.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const verifyTerapiasLink = (usuarioId, { documento, solicitanteId } = {}) => {
  if (!usuarioId) {
    return Promise.reject(new Error('Usuario no válido.'));
  }

  return api
    .post('/especialista_verificar_terapias.php', {
      usuario_id: usuarioId,
      documento: documento || '',
      solicitante_id: solicitanteId || 0,
      accion: 'verificar',
    })
    .then((response) => getApiData(response, 'No se pudo verificar la vinculación.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const unlinkTerapias = (usuarioId) => {
  if (!usuarioId) {
    return Promise.reject(new Error('Usuario no válido.'));
  }

  return api
    .post('/especialista_verificar_terapias.php', {
      usuario_id: usuarioId,
      accion: 'desvincular',
    })
    .then((response) => getApiData(response, 'No se pudo desvincular.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const getDocumentLabel = (profile) => {
  if (!profile || !profile.documento_numero) {
    return '';
  }

  const tipos = {
    cedula: 'Cédula',
    pasaporte: 'Pasaporte',
    otro: 'Documento',
  };

  const tipo = tipos[profile.documento_tipo] || 'Documento';
  return `${tipo}: ${profile.documento_numero}`;
};

export const formatProfileDate = (value) => {
  if (!value) {
    return '—';
  }

  const parts = value.split('-');
  if (parts.length !== 3) {
    return value;
  }

  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

export const getProfileSubtitle = (profile) => {
  if (!profile) {
    return '';
  }

  if (profile.tipo === 'administrativo') {
    const hasSuperAdmin = (profile.roles || []).some((role) => role.codigo === 'super_admin');
    if (hasSuperAdmin) {
      return 'Super administrador';
    }
    return 'Administrador';
  }

  if (profile.tipo === 'especialista') {
    return 'Especialista';
  }

  if (profile.genero === 'mujer') {
    return 'Madre/Tutora';
  }

  if (profile.genero === 'hombre') {
    return 'Padre/Tutor';
  }

  return 'Paciente';
};

export const getGenderLabel = (genero) => {
  if (genero === 'mujer') {
    return 'Femenino';
  }

  if (genero === 'hombre') {
    return 'Masculino';
  }

  return '—';
};

export const getChildAgeLabel = (fechaNacimiento) => {
  if (!fechaNacimiento) {
    return 'Sin edad';
  }

  const parts = fechaNacimiento.split('-');
  if (parts.length !== 3) {
    return 'Sin edad';
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  const birthDate = new Date(year, month, day);

  if (Number.isNaN(birthDate.getTime())) {
    return 'Sin edad';
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  if (age < 0) {
    return 'Sin edad';
  }

  if (age === 1) {
    return '1 año';
  }

  return `${age} años`;
};

export const getChildGenderLabel = (genero) => {
  if (genero === 'nina' || genero === 'mujer') {
    return 'Femenino';
  }

  if (genero === 'nino' || genero === 'hombre') {
    return 'Masculino';
  }

  return '—';
};

export const getChildrenLabel = (profile) => {
  if (!profile) {
    return '—';
  }

  if (profile.sin_hijos) {
    return 'Sin pacientes';
  }

  const total = profile.total_hijos || profile.total_pacientes || 0;

  if (total === 0) {
    return 'Sin pacientes registrados';
  }

  if (total === 1) {
    return '1 paciente';
  }

  return `${total} pacientes`;
};

export const searchPatients = (solicitanteId, q) => {
  if (!solicitanteId) {
    return Promise.reject(new Error('Debes iniciar sesión.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La búsqueda requiere conexión con el servidor.'));
  }

  return api
    .post('/pacientes_buscar.php', {
      solicitante_id: solicitanteId,
      q: q || '',
    })
    .then((response) => getApiData(response, 'No se pudo buscar pacientes.'))
    .then((data) => data.pacientes || [])
    .catch((error) => {
      throw parseApiError(error);
    });
};
